// components/common/BaseResultsPage.js
import React, {useEffect, useState} from 'react';
import {useOrganization, useOrganizationFeatures} from '../../hooks/useOrganization';
import {useResultsData} from '../../hooks/useResultsData';
import {useHealthMetrics} from '../../hooks/useHealthMetrics';
import {AnimationLoadingScreenWithProgressBar} from '../../../../components/loading/loadingScreen';
import {SimpleLoader} from '../../../../components/loading/simpleLoader';
import dayjs from 'dayjs';

export const BaseResultsPage = ({children, apiClients, customLoader = null, onDataLoaded = () => {}}) => {
  const organization = useOrganization();
  const features = useOrganizationFeatures(organization.type);
  const {results, demographics, symptoms, loading, error} = useResultsData(organization.type, apiClients);

  // Calculate age from demographics
  const age = demographics?.date_of_birth ? dayjs(demographics.date_of_birth).diff(dayjs(), 'year') * -1 : null;

  // Get health metrics
  const healthMetrics = useHealthMetrics(results, age);

  // Track game completion status
  const [isGameCompleted, setIsGameCompleted] = useState(false);
  useEffect(() => {
    const isGamePlayed = sessionStorage.getItem('isGamePlayed') === 'true';
    setIsGameCompleted(isGamePlayed);
  }, []);

  // Notify parent when data is loaded
  useEffect(() => {
    if (!loading && results) {
      onDataLoaded({
        organization,
        features,
        results,
        demographics,
        symptoms,
        healthMetrics,
        age,
        isGameCompleted,
      });
    }
  }, [loading, results]);

  if (loading) {
    if (customLoader) {
      return customLoader;
    }

    // Organization-specific loaders
    if (organization.flags.isAtpl || organization.flags.isHelloHealth) {
      return <SimpleLoader loadingText="Loading..." textColor="#F15C30" />;
    }

    return <AnimationLoadingScreenWithProgressBar title="Loading..." secondsToLoad={60} isAlmostComplete={!loading} />;
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error loading results</h2>
        <p>{error}</p>
      </div>
    );
  }

  // Pass all necessary data to children
  const childProps = {
    organization,
    features,
    results,
    demographics,
    symptoms,
    healthMetrics,
    age,
    isGameCompleted,
    loading,
  };

  return <>{React.isValidElement(children) ? React.cloneElement(children, childProps) : null}</>;
};
